import { toast } from 'sonner'
import { uploadFile } from '@/lib/firebase'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import axios, { endpoints } from '@/lib/axios'
import { Button } from '@/components/ui/button'
import React, { useRef, useState } from 'react'
import { useBoolean } from '@/hooks/use-boolean'
import { Checkbox } from '@/components/ui/checkbox'
import { useDeepEffect } from '@/hooks/use-deep-effect'
import ConfirmDialog from '@/components/share-components/confirm-dialog'
import LoadingSection from '@/components/loading-indicator/loading-section'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  PlatformConfig,
  PlatformConfigName,
} from '@/types/platform-config.type'
import {
  ImagePlus,
  Edit,
  Trash2,
  Eye,
  Plus,
  X,
  Loader2,
  ExternalLink,
} from 'lucide-react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'

interface BannerImageData {
  id?: string
  file: File | null
  previewUrl: string | null
  fileName: string
  href?: string
  isBlank?: boolean
}

interface BannerImagesState {
  main: BannerImageData[]
  market: BannerImageData[]
}

type BannerType = 'main' | 'market'
type DialogMode = 'add' | 'edit' | 'view' | null

interface BannerDialogData {
  type: BannerType
  mode: DialogMode
  index?: number
  image?: BannerImageData
}

export default function BannersView() {
  const isFetching = useBoolean(true)
  const isDeleting = useBoolean(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const [images, setImages] = useState<BannerImagesState>({
    main: [],
    market: [],
  })

  const [dialogData, setDialogData] = useState<BannerDialogData>({
    type: 'main',
    mode: null,
  })

  const [tempImage, setTempImage] = useState<BannerImageData | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const reload = useBoolean()
  const isFirstInit = useBoolean(true)

  const openDialog = (data: BannerDialogData) => {
    setDialogData(data)
    if ((data.mode === 'edit' || data.mode === 'view') && data.image) {
      setTempImage({ ...data.image })
    } else if (data.mode === 'add') {
      setTempImage({
        file: null,
        previewUrl: null,
        fileName: '',
        href: '',
        isBlank: false,
      })
    }
  }

  const closeDialog = () => {
    setDialogData({ type: 'main', mode: null })
    setTempImage(null)
    if (inputRef.current) {
      inputRef.current.value = ''
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return

    if (!file.type.startsWith('image/')) {
      toast.error('กรุณาเลือกรูปภาพเท่านั้น')
      return
    }

    setTempImage((prev) => ({
      ...prev!,
      file,
      previewUrl: URL.createObjectURL(file),
      fileName: file.name,
    }))
  }

  const handleInputChange = (field: string, value: string | boolean) => {
    setTempImage((prev) => ({
      ...prev!,
      [field]: value,
    }))
  }

  const saveBannersToAPI = async (
    type: BannerType,
    bannersData: BannerImageData[],
  ) => {
    const banners = []

    for (const img of bannersData) {
      let imageUrl = img.previewUrl

      if (img.file) {
        const fileName = `banner_${type}_${Date.now()}_${Math.random().toString(36).substr(2, 9)}.${img.file.name.split('.').pop()}`
        imageUrl = await uploadFile(img.file, 'platform-configs', fileName)
      }

      banners.push({
        url: imageUrl,
        href: img.href || '',
        isBlank: img.isBlank || false,
      })
    }

    const configName =
      type === 'main'
        ? PlatformConfigName.BANNER_MAIN
        : PlatformConfigName.BANNER_MARKET

    await axios.post<PlatformConfig>(endpoints.platformConfig.create, {
      name: configName,
      value: JSON.stringify(banners),
    })
  }

  const handleSaveImage = async () => {
    // ตรวจสอบว่าเป็น url หรือไม่
    const urlPattern = /^(https?:\/\/)[^\s/$.?#].[^\s]*$/i

    if (!!tempImage?.href && !urlPattern.test(tempImage?.href || '')) {
      toast.error('กรุณากรอก URL ที่ถูกต้อง เช่น https://example.com')
      return
    }

    if (!tempImage || !tempImage.previewUrl) {
      toast.error('กรุณาเลือกรูปภาพ')
      return
    }

    const maxImages = 10
    const currentImages = images[dialogData.type]

    if (dialogData.mode === 'add') {
      if (currentImages.length >= maxImages) {
        toast.error(`สามารถเพิ่มรูปได้สูงสุด ${maxImages} รูป`)
        return
      }
    }

    setIsSubmitting(true)

    try {
      let newImages = [...currentImages]

      if (dialogData.mode === 'add') {
        newImages = [...currentImages, tempImage]
      } else if (dialogData.mode === 'edit' && dialogData.index !== undefined) {
        newImages[dialogData.index] = tempImage
      }

      // Save to API immediately
      await saveBannersToAPI(dialogData.type, newImages)

      // Update local state
      setImages((prev) => ({
        ...prev,
        [dialogData.type]: newImages.map((img) => ({
          ...img,
          file: null, // Clear file after upload
        })),
      }))

      const successMessage =
        dialogData.mode === 'add' ? 'เพิ่มรูปภาพสำเร็จ' : 'แก้ไขรูปภาพสำเร็จ'
      toast.success(successMessage)
      reload.onToggle()
      closeDialog()
    } catch (error) {
      console.error('Save error:', error)
      toast.error('เกิดข้อผิดพลาดในการบันทึก')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleDeleteImage = async (type: BannerType, index: number) => {
    try {
      isDeleting.onTrue()
      const newImages = images[type].filter((_, i) => i !== index)

      // Save to API immediately
      await saveBannersToAPI(type, newImages)

      // Update local state
      setImages((prev) => ({
        ...prev,
        [type]: newImages,
      }))

      toast.success('ลบรูปภาพสำเร็จ')
      reload.onToggle()
    } catch (error) {
      console.error('Delete error:', error)
      toast.error('เกิดข้อผิดพลาดในการลบ')
    } finally {
      isDeleting.onFalse()
    }
  }

  useDeepEffect(() => {
    const fetchPlatformConfig = async () => {
      try {
        if (isFirstInit.value) isFetching.onTrue()
        const { data } = await axios.get<PlatformConfig[]>(
          endpoints.platformConfig.list,
          {
            params: {
              name: `${PlatformConfigName.BANNER_MAIN}, ${PlatformConfigName.BANNER_MARKET}`,
            },
          },
        )

        const mainConfig = data.find(
          (config) => config.name === PlatformConfigName.BANNER_MAIN,
        )
        const marketConfig = data.find(
          (config) => config.name === PlatformConfigName.BANNER_MARKET,
        )

        const parseExistingImages = (
          value: string | undefined,
        ): BannerImageData[] => {
          if (!value) return []

          try {
            // Try to parse as JSON (new format with href and isBlank)
            const banners = JSON.parse(value)
            if (Array.isArray(banners)) {
              return banners.map((banner, index) => ({
                id: `existing_${index}`,
                file: null,
                previewUrl: banner.url,
                fileName: `banner_${index + 1}`,
                href: banner.href || '',
                isBlank: banner.isBlank || false,
              }))
            }
          } catch {
            // Fallback to old format (comma-separated URLs)
            const urls = value.split(',').filter((url) => url.trim())
            return urls.map((url, index) => ({
              id: `existing_${index}`,
              file: null,
              previewUrl: url.trim(),
              fileName: `banner_${index + 1}`,
              href: '',
              isBlank: false,
            }))
          }

          return []
        }

        setImages({
          main: parseExistingImages(mainConfig?.value),
          market: parseExistingImages(marketConfig?.value),
        })
      } catch (error) {
        console.error('Failed to fetch platform config:', error)
        toast.error('เกิดข้อผิดพลาดในการโหลดข้อมูลแบนเนอร์')
      } finally {
        isFetching.onFalse()
        isFirstInit.onFalse()
      }
    }
    fetchPlatformConfig()
  }, [reload.value])

  return (
    <LoadingSection loading={isFetching.value}>
      <div className="space-y-6">
        {/* Tables */}
        <BannerTable
          openDialog={openDialog}
          handleDeleteImage={handleDeleteImage}
          title="รูปแบนเนอร์หน้าหลัก"
          data={images.main}
          type="main"
          deleting={isDeleting.value}
        />

        <BannerTable
          openDialog={openDialog}
          handleDeleteImage={handleDeleteImage}
          title="รูปแบนเนอร์หน้ามาร์เก็ต"
          data={images.market}
          type="market"
          deleting={isDeleting.value}
        />

        {/* Dialog */}
        <Dialog open={dialogData.mode !== null} onOpenChange={closeDialog}>
          <DialogContent
            className="w-[95vw] max-w-2xl max-h-[90vh] overflow-y-auto"
            aria-describedby=""
          >
            <DialogHeader>
              <DialogTitle className="text-base sm:text-lg">
                {dialogData.mode === 'add' && 'เพิ่มรูปแบนเนอร์'}
                {dialogData.mode === 'edit' && 'แก้ไขรูปแบนเนอร์'}
                {dialogData.mode === 'view' && 'ดูรูปแบนเนอร์'}
                {dialogData.type === 'main' ? 'หน้าหลัก' : 'หน้ามาร์เก็ต'}
              </DialogTitle>
            </DialogHeader>

            <div className="space-y-4 px-1">
              {/* Image Preview */}
              {tempImage?.previewUrl && (
                <div
                  className={`w-full ${
                    dialogData.type === 'main'
                      ? 'aspect-[375/250] sm:aspect-[375/250]'
                      : 'aspect-[343/144] sm:aspect-[343/144]'
                  } border rounded-md overflow-hidden bg-muted relative`}
                >
                  <img
                    src={tempImage.previewUrl}
                    alt="Preview"
                    className="w-full h-full object-cover"
                  />

                  {dialogData.mode !== 'view' && (
                    <div className="absolute right-2 top-2">
                      <Button
                        variant="destructive"
                        size="sm"
                        className="h-8 w-8 p-0"
                        onClick={() =>
                          setTempImage((prev) => ({
                            ...prev!,
                            previewUrl: null,
                            file: null,
                          }))
                        }
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </div>
              )}

              {/* File Input */}
              {!tempImage?.previewUrl && dialogData.mode !== 'view' && (
                <div
                  className={`flex flex-col items-center justify-center border border-dashed border-muted-foreground rounded-md p-4 sm:p-8 text-center cursor-pointer hover:bg-muted/50 transition-colors ${
                    dialogData.type === 'main'
                      ? 'aspect-[375/250] sm:aspect-[375/250]'
                      : 'aspect-[343/144] sm:aspect-[343/144]'
                  }`}
                  onClick={() => inputRef.current?.click()}
                >
                  <ImagePlus className="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 text-muted-foreground" />
                  <p className="text-xs sm:text-sm text-muted-foreground px-2">
                    {tempImage?.previewUrl
                      ? 'คลิกเพื่อเปลี่ยนรูป'
                      : 'คลิกเพื่อเลือกรูป'}
                  </p>
                  <input
                    ref={inputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleFileChange}
                  />
                </div>
              )}

              {/* Link Settings */}
              {tempImage && dialogData.mode !== 'view' && (
                <div className="space-y-4 mb-4">
                  <div className="space-y-2">
                    <Label htmlFor="href" className="text-sm">
                      ลิงก์
                    </Label>
                    <Input
                      id="href"
                      type="text"
                      placeholder="https://example.com"
                      value={tempImage.href || ''}
                      onChange={(e) =>
                        handleInputChange('href', e.target.value)
                      }
                      className="text-sm"
                    />
                  </div>

                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="isBlank"
                      checked={tempImage.isBlank || false}
                      onCheckedChange={(checked) =>
                        handleInputChange('isBlank', checked)
                      }
                    />
                    <Label
                      htmlFor="isBlank"
                      className="text-xs sm:text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                    >
                      ลิงก์ภายนอก
                    </Label>
                  </div>
                </div>
              )}

              {/* File Info */}
              {tempImage && (
                <div className="text-xs sm:text-sm text-muted-foreground space-y-1 bg-muted/30 p-3 rounded-md">
                  <p className="break-all">ชื่อไฟล์: {tempImage.fileName}</p>
                  {dialogData.mode === 'view' && (
                    <>
                      {tempImage.href && (
                        <p className="flex items-start gap-1 break-all">
                          <ExternalLink className="w-3 h-3 mt-[2px] flex-shrink-0" />
                          <span>ลิงก์: {tempImage.href}</span>
                        </p>
                      )}
                      {tempImage.isBlank && <p>ลิงก์ภายนอก: ใช่</p>}
                    </>
                  )}
                </div>
              )}
            </div>

            <DialogFooter className="flex-col-reverse sm:flex-row gap-2">
              <Button
                variant="outline"
                onClick={closeDialog}
                className="w-full sm:w-auto"
                disabled={isSubmitting}
              >
                ยกเลิก
              </Button>
              {dialogData.mode !== 'view' && (
                <Button
                  onClick={handleSaveImage}
                  className="w-full sm:w-auto"
                  disabled={isSubmitting}
                >
                  {isSubmitting && (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  )}
                  {dialogData.mode === 'add' ? 'เพิ่ม' : 'บันทึก'}
                </Button>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </LoadingSection>
  )
}

const BannerTable = ({
  title,
  data,
  type,
  deleting,
  openDialog,
  handleDeleteImage,
}: {
  title: string
  data: BannerImageData[]
  type: BannerType
  deleting: boolean
  openDialog: (data: BannerDialogData) => void
  handleDeleteImage: (type: BannerType, index: number) => void
}) => {
  const [confirmDelete, setConfirmDelete] = useState({
    open: false,
    fileName: '',
    type: null as BannerType | null,
    index: null as number | null,
  })

  return (
    <Card className="mb-6">
      <CardHeader className="flex flex-row flex-wrap sm:flex-nowrap gap-y-3 items-center justify-between">
        <CardTitle className="text-lg font-semibold">{title}</CardTitle>
        <div className="flex items-center gap-2">
          <Badge variant="outline">{data.length}/5 รูป</Badge>
          <Button
            size="sm"
            onClick={() => openDialog({ type, mode: 'add' })}
            disabled={data.length >= 5}
          >
            <Plus className="w-4 h-4 mr-1" />
            เพิ่มรูป
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {data.length === 0 ? (
          <div className="text-center py-8 text-muted-foreground">
            ไม่มีรูปแบนเนอร์
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-16">ลำดับ</TableHead>
                <TableHead className="w-24">รูปภาพ</TableHead>
                <TableHead>ชื่อไฟล์</TableHead>
                <TableHead>ลิงก์</TableHead>
                <TableHead className="w-32">จัดการ</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((image, index) => (
                <TableRow key={image.id || index}>
                  <TableCell className="font-medium">{index + 1}</TableCell>
                  <TableCell>
                    <div className="w-16 h-10 bg-muted rounded overflow-hidden">
                      {image.previewUrl && (
                        <img
                          src={image.previewUrl}
                          alt={image.fileName}
                          className="w-full h-full object-cover"
                        />
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="truncate max-w-48">
                    {image.fileName}
                  </TableCell>
                  <TableCell>
                    {image.href ? (
                      <div className="flex items-center gap-1">
                        <ExternalLink className="w-3 h-3" />
                        <span
                          className="text-xs truncate max-w-64"
                          title={image.href}
                        >
                          {image.href}
                        </span>
                        {image.isBlank && (
                          <Badge
                            variant="secondary"
                            className="text-[11px] ms-2 bg-neutral-300 p-1"
                          >
                            ลิงก์ภายนอก
                          </Badge>
                        )}
                      </div>
                    ) : (
                      <span className="text-muted-foreground text-sm">-</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          openDialog({
                            type,
                            mode: 'view',
                            index,
                            image,
                          })
                        }
                      >
                        <Eye className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() =>
                          openDialog({
                            type,
                            mode: 'edit',
                            index,
                            image,
                          })
                        }
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="text-red-600 hover:text-red-700"
                        onClick={() => {
                          setConfirmDelete({
                            open: true,
                            fileName: image.fileName,
                            type,
                            index,
                          })
                          // handleDeleteImage(type, index)
                        }}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>

      {/* Confirm Delete Dialog */}
      <ConfirmDialog
        open={confirmDelete.open}
        onOpenChange={(open) =>
          setConfirmDelete({
            open,
            fileName: '',
            type: null as BannerType | null,
            index: null as number | null,
          })
        }
        title="ยืนยันการลบ"
        description={`คุณต้องการลบแบนเนอร์ ${confirmDelete?.fileName} หรือไม่?`}
        confirmText="ลบ"
        cancelText="ยกเลิก"
        variant="destructive"
        onConfirm={() => {
          if (confirmDelete.type && typeof confirmDelete.index === 'number') {
            handleDeleteImage(confirmDelete.type, confirmDelete.index)
          }
        }}
        loading={deleting}
      />
    </Card>
  )
}
